import { useTranslation } from '@/contexts/i18n-context';
import React, { useRef, useState } from 'react';
import { Link } from '@/util/react-router-dom';
import { Button } from '@/components/button';
import styles from './navigation-stepper.module.css';
import clsx from 'clsx';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { EncounterStatusIdsType } from '@/api/api.schemas';
import { useOutsideClick } from '@/hooks/use-outside-click';
import { Separator } from '../separator';

export interface Step {
  readonly name: string;
  path: string;
  readonly isCompleted: boolean;
  readonly isActive: boolean;
  readonly statusId?: EncounterStatusIdsType;
  readonly nonNavigable?: boolean;
  readonly children?: readonly Step[];
}

interface NavigationStepperProps {
  title?: string;
  steps: readonly Step[];
  className?: string;
  isNonInteractive?: boolean;
}

export function NavigationStepper({
  title,
  steps,
  className,
  isNonInteractive,
}: NavigationStepperProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const hasNestedSteps = steps.some((step) => step.children);

  useOutsideClick(ref, () => setIsOpen(false));

  const stepsInfo = currentStepInfo(steps);
  if (!stepsInfo) {
    return null;
  }

  const { currentStep, currentStepIndex, totalSteps } = stepsInfo;
  const currentStepTitle = currentStep.nonNavigable
    ? t('navigationStepper.stepperButtonTitle', {
        title: title ?? currentStep.name,
      })
    : t('navigationStepper.stepperButton', {
        title: title ?? currentStep.name,
        currentStepIndex: currentStepIndex,
        totalSteps: totalSteps,
      });

  return (
    <div ref={ref} className={clsx(styles.stepperContainer, className)}>
      {isNonInteractive ? (
        <p className={styles.stepperTitle}>{currentStepTitle}</p>
      ) : (
        <Button
          variant="link"
          size="M"
          color="accent"
          className="overline"
          rightIcon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          onClick={() => setIsOpen(!isOpen)}
        >
          {currentStepTitle}
        </Button>
      )}
      {isOpen && (
        <ol className={styles.stepperList}>
          {steps.map((step, index) => (
            <React.Fragment key={step.name}>
              <NavigationStepperItem
                key={step.name}
                step={step}
                indexLabel={`${index + 1}.`}
                hasNestedSteps={hasNestedSteps}
              />
            </React.Fragment>
          ))}
        </ol>
      )}
    </div>
  );
}

interface NavigationStepperItemProps {
  step: Step;
  indexLabel: string;
  hasNestedSteps: boolean;
  isChild?: boolean;
}

function NavigationStepperItem({
  step,
  indexLabel,
  hasNestedSteps,
  isChild = false,
}: NavigationStepperItemProps) {
  const { t } = useTranslation();

  const stepContent = t('navigationStepper.stepperLink', {
    indexLabel,
    label: step.name,
  });

  const isHeader = !isChild && hasNestedSteps;
  const isDisabled = !step.isCompleted && !step.isActive;

  if (isHeader) {
    return (
      <>
        <li key={step.name}>
          <div className={clsx(styles.header, 'overline')}>
            <span aria-disabled={true}>{stepContent}</span>
            <Separator />
          </div>
        </li>
        {step.children?.map((child, childIndex) => (
          <NavigationStepperItem
            key={child.name}
            step={child}
            hasNestedSteps={hasNestedSteps}
            indexLabel={`${indexLabel}${childIndex + 1}.`}
            isChild
          />
        ))}
      </>
    );
  }

  if (isDisabled) {
    return (
      <li key={step.name}>
        <span
          className={clsx(styles.stepperLink, styles.disabled, { [styles.indentation]: isChild })}
          role="link"
          aria-disabled={true}
        >
          {stepContent}
        </span>
      </li>
    );
  }

  return (
    <li key={step.name}>
      <Link
        to={step.nonNavigable ? '#' : step.path}
        className={clsx(styles.stepperLink, {
          [styles.active]: step.isActive && !step.children,
          [styles.activeParent]: step.isActive && step.children,
          [styles.completed]: step.isCompleted,
          [styles.indentation]: isChild,
          [styles.nonNavigable]: step.nonNavigable,
        })}
      >
        <span>{stepContent}</span>
        {step.isCompleted && <MaterialSymbolIcon name="check" />}
      </Link>
    </li>
  );
}

function currentStepInfo(steps: readonly Step[]) {
  const currentStep = steps.find((step) => step.isActive);

  if (!currentStep) {
    return undefined;
  }

  if (currentStep.children) {
    const activeChild = currentStep.children.find((child) => child.isActive);
    const childIndex = currentStep.children.findIndex((child) => child.isActive);

    if (!activeChild) {
      return undefined;
    }

    return {
      currentStep,
      currentStepIndex: childIndex + 1,
      totalSteps: currentStep.children.length,
    };
  }

  const rootIndex = steps.findIndex((step) => step.isActive);

  return {
    currentStep,
    currentStepIndex: rootIndex + 1,
    totalSteps: steps.length,
  };
}
